import axios, { AxiosError } from 'axios';

import Localize from 'app/src/localize/Localize';

import { updateRegisterUI } from './view';

function dispatchRegisterSwitchedEvent(payload: RegisterSwitchedEventPayload) {
  return window.merchantos.asyncSubscribe.notifySubscribers('register.register_switched', payload);
}

/**
 * keepRegisterLocked; true will lock to computer, false will release the lock
 */
export async function selectRegister(
  registerID: string,
  registerName: string,
  status: RegisterStatus
): Promise<RegisterAPIResponse | void> {
  // Not available through mobile app
  if (window.isGriffin()) {
    window.StencilWebAPI.shop.setRegister(registerID);
    return;
  }

  // If register is closed, open it before we can switch to it
  if (status === 'closed') {
    // Quickly refresh the UI to glimpse at the future state
    updateRegisterUI(registerID, false);

    // Navigate to the open register page
    window.merchantos.utils.openUrl(
      `register.php?form_name=open_another_register&register_id=${registerID}`
    );
    return;
  }

  try {
    window.showNotifier('working');
    const { data } = await axios.post<RegisterAPIResponse>(`/admin/registers/${registerID}/select`);
    window.merchantos.session.clearCache();
    await window.merchantos.session.init();
    if (registerName) {
      dispatchRegisterSwitchedEvent({
        registerID: data?.registerID,
        registerName,
        canLockRegister: data?.canLockRegister,
      });
    }
    updateRegisterUI(
      `${data?.registerID ?? ''}`,
      data?.isRegisterLocked ?? false,
      data?.canLockRegister ?? true
    );
    return data;
  } catch (error) {
    handleError(
      Localize.translate(`An error occurred while switching register, please try again later`)
    )(error);
  } finally {
    window.hideNotifier('working');
  }
}

export async function setRegisterLock(
  registerID: string,
  lockRegister: boolean = true
): Promise<RegisterAPIResponse | void> {
  // Not available through mobile app
  if (window.isGriffin()) {
    window.StencilWebAPI.shop.setRegister(registerID);
    return;
  }

  const data = await axios
    .post<RegisterAPIResponse>(`/admin/registers/${lockRegister ? 'lock' : 'unlock'}`)
    .then((response) => response?.data)
    .catch(
      handleError(
        Localize.translate(
          `An error occurred while ${
            lockRegister ? 'locking' : 'unlocking'
          } register, please try again later`
        )
      )
    );

  updateRegisterUI(
    `${data?.registerID ?? ''}`,
    data?.isRegisterLocked ?? false,
    data?.canLockRegister ?? true
  );

  return data;
}

const handleError =
  (defaultMessage?: string) =>
  (error: AxiosError<RegisterAPIResponse>): RegisterAPIResponse | undefined => {
    const message = error?.response?.statusText ?? defaultMessage;
    if (message) {
      window.flashNotifier('messages', message, 15);
    }
    return error?.response?.data;
  };
